import {
  getCustomerTags,
  getCustomers,
  setCustomerSelection,
  setCustomerTagsSelection,
  setCustomerSegments,
  setCustomerSegmentSelection,
} from "../slice/customerSlice";
import { apiSlice } from "./apiSlice";

export const customerApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({
    getCustomers: builder.query({
      providesTags: ["Customer"],
      query: params =>
        params ? `/api/customer/v1/customer/?${params}` : "/api/customer/v1/customer/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(getCustomers(result.data))).catch(error => {});
      },
    }),
    getCustomersSelection: builder.query({
      providesTags: ["Customer"],
      query: () => "/api/customer/v1/customer/selection/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(setCustomerSelection(result.data)))
          .catch(error => {});
      },
    }),
    getCustomerById: builder.query({
      providesTags: ["Customer"],
      query: id => `/api/customer/v1/customer/${id}/`,
    }),
    getCustomerCodeOptions: builder.query({
      providesTags: ["Customer"],
      query: params => ({ url: "/api/customer/v1/customer/code_options/", params }),
    }),
    createCustomer: builder.mutation({
      invalidatesTags: ["Customer"],
      query: data => ({
        url: "/api/customer/v1/customer/",
        method: "POST",
        body: data,
      }),
    }),
    updateCustomer: builder.mutation({
      invalidatesTags: ["Customer"],
      query: ({ id, data }) => ({
        url: `/api/customer/v1/customer/${id}/`,
        method: "PATCH",
        body: data,
      }),
    }),
    getCustomerContacts: builder.query({
      query: customerId => ({
        url: "/api/customer/v1/customer_contact/",
        params: { customer_id: customerId, limit: 1000 },
      }),
    }),
    getCustomerAddresses: builder.query({
      providesTags: ["CustomerAddress"],
      query: customerId => ({
        url: "/api/customer/v1/customer_address/",
        params: { customer_id: customerId, limit: 1000 },
      }),
    }),
    createCustomerAddress: builder.mutation({
      invalidatesTags: ["CustomerAddress"],
      query: data => ({
        url: "/api/customer/v1/customer_address/",
        method: "POST",
        body: data,
      }),
    }),
    getCustomerTags: builder.query({
      query: params =>
        params ? `/api/customer/v1/customer_tag/?${params}` : "/api/customer/v1/customer_tag/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled.then(result => dispatch(getCustomerTags(result.data))).catch(error => {});
      },
    }),
    getCustomerTagsSelection: builder.query({
      query: () => "/api/customer/v1/customer_tag/selection/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(setCustomerTagsSelection(result.data)))
          .catch(error => {});
      },
    }),
    getCustomerTagById: builder.query({
      query: id => `/api/customer/v1/customer_tag/${id}/`,
    }),
    createCustomerTag: builder.mutation({
      query: data => ({
        url: "/api/customer/v1/customer_tag/",
        method: "POST",
        body: data,
      }),
    }),
    updateCustomerTag: builder.mutation({
      query: ({ id, data }) => ({
        url: `/api/customer/v1/customer_tag/${id}/`,
        method: "PATCH",
        body: data,
      }),
    }),
    getCustomerSegments: builder.query({
      query: params =>
        params
          ? `/api/customer/v1/customer_segment/?${params}`
          : "/api/customer/v1/customer_segment/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(setCustomerSegments(result.data)))
          .catch(error => {});
      },
      providesTags: ["customerSegmentUpdate"],
    }),
    getCustomerSegmentSelection: builder.query({
      query: () => "/api/customer/v1/customer_segment/selection/",
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(result => dispatch(setCustomerSegmentSelection(result.data)))
          .catch(error => {});
      },
    }),
    getCustomerSegmentById: builder.query({
      query: id => `/api/customer/v1/customer_segment/${id}/`,
    }),
    getCustomerSegmentPreview: builder.query({
      query: params =>
        params
          ? `/api/customer/v1/customer_segment/preview/?${params}`
          : "/api/customer/v1/customer_segment/preview/",
    }),
    createCustomerSegment: builder.mutation({
      query: data => ({
        url: "/api/customer/v1/customer_segment/",
        method: "POST",
        body: data,
      }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(res => dispatch(customerApiSlice.util.invalidateTags(["customerSegmentUpdate"])))
          .catch(err => {});
      },
    }),
    updateCustomerSegment: builder.mutation({
      query: ({ id, data }) => ({
        url: `/api/customer/v1/customer_segment/${id}/`,
        method: "PATCH",
        body: data,
      }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(res => dispatch(customerApiSlice.util.invalidateTags(["customerSegmentUpdate"])))
          .catch(err => {});
      },
    }),
    deleteCustomerSegment: builder.mutation({
      query: id => ({
        url: `/api/customer/v1/customer_segment/${id}/`,
        method: "DELETE",
      }),
      onQueryStarted: (_, { dispatch, queryFulfilled }) => {
        queryFulfilled
          .then(res => dispatch(customerApiSlice.util.invalidateTags(["customerSegmentUpdate"])))
          .catch(err => {});
      },
    }),
    getCustomerStatesSelection: builder.query({
      query: paramData => {
        return paramData?.params
          ? `/api/customer/v1/customer_address/states/?countries=${paramData.params}`
          : "";
      },
    }),
    getCustomerCitiesSelection: builder.query({
      query: ({ params } = {}) => {
        const queryParams = new URLSearchParams();
        if (params) {
          Object.entries(params).forEach(([key, value]) => {
            if (Array.isArray(value)) {
              value.forEach(v => queryParams.append(key, v));
            } else if (value) {
              queryParams.append(key, value);
            }
          });
        }
        const qs = queryParams.toString();
        return `/api/customer/v1/customer_address/cities/${qs ? `?${qs}` : ""}`;
      },
    }),
    getCustomerPostcodesSelection: builder.query({
      query: ({ params } = {}) => {
        const queryParams = new URLSearchParams();
        if (params) {
          Object.entries(params).forEach(([key, value]) => {
            if (Array.isArray(value)) {
              value.forEach(v => queryParams.append(key, v));
            } else if (value) {
              queryParams.append(key, value);
            }
          });
        }
        const qs = queryParams.toString();
        return `/api/customer/v1/customer_address/post_codes/${qs ? `?${qs}` : ""}`;
      },
    }),
  }),
});

export const {
  useGetCustomersQuery,
  useGetCustomersSelectionQuery,
  useLazyGetCustomersSelectionQuery,
  useGetCustomerByIdQuery,
  useGetCustomerCodeOptionsQuery,
  useLazyGetCustomerCodeOptionsQuery,
  useCreateCustomerMutation,
  useUpdateCustomerMutation,
  useGetCustomerContactsQuery,
  useGetCustomerAddressesQuery,
  useCreateCustomerAddressMutation,
  useGetCustomerTagsQuery,
  useGetCustomerTagsSelectionQuery,
  useLazyGetCustomerTagsSelectionQuery,
  useGetCustomerTagByIdQuery,
  useCreateCustomerTagMutation,
  useUpdateCustomerTagMutation,
  useGetCustomerSegmentsQuery,
  useGetCustomerSegmentSelectionQuery,
  useLazyGetCustomerSegmentSelectionQuery,
  useGetCustomerSegmentByIdQuery,
  useGetCustomerSegmentPreviewQuery,
  useCreateCustomerSegmentMutation,
  useUpdateCustomerSegmentMutation,
  useDeleteCustomerSegmentMutation,
  useGetCustomerStatesSelectionQuery,
  useGetCustomerCitiesSelectionQuery,
  useGetCustomerPostcodesSelectionQuery,
} = customerApiSlice;
